import { reportLog } from '@/api/log';
import type { LogReportData, LogEventExt } from '@/types/log';

/**
 * Device ID 存储的 localStorage key
 */
const DEVICE_ID_KEY = 'felo_device_id';

/**
 * 生成 UUID v4
 */
const generateUUID = (): string => {
  // 使用 crypto API 生成 UUID（现代浏览器支持）
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return crypto.randomUUID();
  }

  // 降级方案：手动生成 UUID v4
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
};

/**
 * 获取或生成 device_id
 * 使用 localStorage 持久化存储
 */
export const getDeviceId = (): string => {
  try {
    let deviceId = localStorage.getItem(DEVICE_ID_KEY);
    if (!deviceId) {
      deviceId = generateUUID();
      localStorage.setItem(DEVICE_ID_KEY, deviceId);
    }
    return deviceId;
  } catch (error) {
    console.error('[LogReporter] Failed to get/set device_id:', error);
    // 如果 localStorage 不可用，返回临时 UUID
    return generateUUID();
  }
};

/**
 * 获取浏览器信息
 */
export const getBrowserInfo = (): {
  type: string;
  version: string;
} => {
  const ua = navigator.userAgent;
  let browserType = 'Unknown';
  let browserVersion = 'Unknown';

  // 检测浏览器类型和版本
  if (ua.includes('Chrome') && !ua.includes('Edg')) {
    browserType = 'Chrome';
    const match = ua.match(/Chrome\/(\d+)/);
    if (match) browserVersion = match[1];
  } else if (ua.includes('Safari') && !ua.includes('Chrome')) {
    browserType = 'Safari';
    const match = ua.match(/Version\/(\d+)/);
    if (match) browserVersion = match[1];
  } else if (ua.includes('Firefox')) {
    browserType = 'Firefox';
    const match = ua.match(/Firefox\/(\d+)/);
    if (match) browserVersion = match[1];
  } else if (ua.includes('Edg')) {
    browserType = 'Edge';
    const match = ua.match(/Edg\/(\d+)/);
    if (match) browserVersion = match[1];
  }

  return { type: browserType, version: browserVersion };
};

/**
 * 获取操作系统类型
 */
export const getOSType = (): string => {
  const ua = navigator.userAgent;
  if (ua.includes('Win')) return 'Windows';
  if (ua.includes('Mac')) return 'macOS';
  if (ua.includes('Linux')) return 'Linux';
  if (ua.includes('Android')) return 'Android';
  if (ua.includes('iOS') || ua.includes('iPhone') || ua.includes('iPad')) return 'iOS';
  return 'Unknown';
};

/**
 * 获取时区
 */
export const getTimezone = (): string => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone;
  } catch (error) {
    console.error('[LogReporter] Failed to get timezone:', error);
    return 'UTC';
  }
};

/**
 * 组装日志数据
 */
export const buildLogData = (
  eventType: string,
  userId: string,
  additionalData?: Record<string, unknown>,
): LogReportData => {
  const browserInfo = getBrowserInfo();
  const deviceId = getDeviceId();

  // 组装扩展信息
  const extData: LogEventExt = {
    current_url: window.location.href,
    source: 'MEMBER_CENTER',
    event_type: eventType,
    trigger_type: 'Manual',
    ...(additionalData && { Data: additionalData }),
  };

  const logData: LogReportData = {
    browser_language: navigator.language || 'en-US',
    browser_type: browserInfo.type,
    browser_version: browserInfo.version,
    device_id: deviceId,
    event_time: Date.now(),
    ext: JSON.stringify(extData),
    os_type: getOSType(),
    report_application: 'webtranslation',
    report_application_branch: import.meta.env.MODE === 'production' ? 'production' : 'development',
    report_application_version: '1.0.0', // 可以从 package.json 读取
    timezone: getTimezone(),
    user_id: userId,
  };

  return logData;
};

/**
 * 上报自定义日志
 *
 * @param eventType - 事件类型
 * @param userId - 用户 ID（可选）
 * @param additionalData - 额外数据（可选）
 */
export const reportCustomLog = async (
  eventType: string,
  userId?: string,
  additionalData?: Record<string, unknown>,
): Promise<void> => {
  // 如果没有 userId，直接返回
  if (!userId) {
    return;
  }

  try {
    const logData = buildLogData(eventType, userId, additionalData);

    // 异步上报，不阻塞主流程
    reportLog(logData)
      .then(() => {
        if (import.meta.env.DEV) {
          console.log(
            '%c[自定义日志] ✅ 日志已上报',
            'color: #9c27b0; font-weight: bold;',
            '\n事件类型:', eventType,
            '\n用户 ID:', userId,
            '\n日志数据:', logData,
          );
        }
      })
      .catch((error) => {
        // 日志上报失败不影响业务流程
        console.error('[自定义日志] 上报失败:', error);
      });
  } catch (error) {
    // 捕获所有错误，确保不影响业务
    console.error('[自定义日志] 组装数据失败:', error);
  }
};
